import type { EditLine } from '@/api/types/orderEdit';

// Client-side mirror of the bounds the order-edit server sends. The server
// decides (every quote and submit is checked again there); this only keeps
// the steppers within what it will accept.

/** Brunch options: 0 or at least 2, and at most one brunch product (step-options.vue). */
export function isBrunch(line: Pick<EditLine, 'product' | 'category'>): boolean {
  return line.category === 'option' && line.product.includes('brunch');
}

/** Locks that never allow a change, whatever the bounds say. */
const FROZEN_LOCKS: ReadonlySet<string> = new Set(['fixed', 'price_unavailable', 'dishes_chosen', 'capacity_unknown']);

/** Whether the customer can change this line at all. */
export function isLineEditable(line: EditLine): boolean {
  if (line.unitCents === null || (line.lock !== null && FROZEN_LOCKS.has(line.lock))) return false;
  return line.max === null || line.max > line.min;
}

/**
 * Highest quantity the line may reach now. `headroom` = seats left in the
 * booked room(s) once the draft is counted (meal lines; null otherwise): all
 * meal lines together stay within the capacity.
 */
export function maxFor(line: EditLine, current: number, headroom: number | null): number {
  const lineMax = line.max ?? Number.POSITIVE_INFINITY;
  return headroom === null ? lineMax : Math.min(lineMax, current + Math.max(0, headroom));
}

/** The quantity a stepper or an "Ajouter" click asks for, brought within the bounds. */
export function clampTarget(line: EditLine, wanted: number, current: number, headroom: number | null): number {
  let value = Math.max(0, Math.floor(wanted));
  if (isBrunch(line) && value === 1) value = current < 1 ? 2 : 0;
  value = Math.min(value, maxFor(line, current, headroom));
  return Math.max(value, line.min);
}

/** First quantity of a line added from 0. */
export function firstQuantity(line: EditLine): number {
  return Math.max(line.min, isBrunch(line) ? 2 : 1);
}

/** 3000 → '30,00 €'. The sign is the sentence's job (« À payer », « À rembourser »). */
export function formatCents(cents: number): string {
  return `${(Math.abs(cents) / 100).toFixed(2).replace('.', ',')} €`;
}

/** '2026-12-12' → '12/12/2026' */
export function formatIsoDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}
